import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  Subject,
  Subscription,
  debounceTime,
  forkJoin,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import {
  Contact,
  ContactSearchFilter,
  LedgerContactsService,
  LedgersService,
  SearchLedgerContacts200Response,
  SearchLedgerContactsRequest,
} from 'ldt-ledger-service-api-v2';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { LedgerDownloadDrawerComponent } from '../ledger-download-drawer/ledger-download-drawer.component';
import { LedgerComponent } from '../ledger.component';
import { Ledger } from '../ledger.types';

type ChangeKind = 'new_company' | 'no_employer';
export type ChangeTab = 'all' | ChangeKind;

/** A detected job change, flattened for display in the overview table. */
interface JobChangeRow {
  id?: string;
  name: string;
  linkedin?: string | null;
  importedCompany?: string | null;
  importedTitle?: string | null;
  newCompany?: string | null;
  newTitle?: string | null;
  kind: ChangeKind;
  /** True when the new job started long enough ago that this isn't a fresh move. */
  olderMove: boolean;
  startedAt?: string | null;
  startedTs: number | null;
  startedAgo?: string | null;
  changedAt?: string | null;
  changedTs: number;
}

@Component({
  selector: 'app-ledger-overview-v2',
  templateUrl: './ledger-overview-v2.component.html',
  styleUrls: ['./ledger-overview-v2.component.scss'],
  providers: [DatePipe],
  standalone: false,
})
export class LedgerOverviewV2Component implements OnInit, AfterViewInit, OnDestroy {
  private static readonly PAGE_SIZE = 100;
  // The search box has to match person OR company, which the search API can't express in
  // one query, so each searched field gets its own capped query and results are merged.
  private static readonly SEARCH_FIELDS = [
    'name',
    'current_position.company.name',
    'imported.company',
  ];
  private static readonly SEARCH_CAP = 200;
  private static readonly MIN_SEARCH_LENGTH = 2;
  private static readonly SEARCH_DEBOUNCE_MS = 400;
  // Quick search fans out fuzzy queries, which get too expensive on very large ledgers
  private static readonly SEARCH_MAX_CONTACTS = 1000000;
  private static readonly SCROLL_LOAD_THRESHOLD_PX = 300;
  // The list always shows the most recent job starts first
  private static readonly DEFAULT_SORT = '-current_position.started_at';
  // Through this day of the month, a 1-month window also includes the previous month
  private static readonly EARLY_MONTH_GRACE_DAY = 14;
  // A move that started more than 6 calendar months ago is flagged as "older" news
  private static readonly OLDER_MOVE_MONTHS = 6;
  // The literal company name the pipeline writes for contacts with no current employer
  private static readonly NO_EMPLOYER_KEYWORD = 'None';

  ledger: Ledger;
  orgId: string;

  // Ledger-level counts (from the ledger record itself)
  contactCount?: number;
  verifiedCount?: number;

  // Stat card counts (server-side filtered counts over all detected changes)
  movedCount?: number;
  noEmployerCount?: number;
  movedRecentCount?: number;

  // Filters
  activeTab: ChangeTab = 'all';
  searchTerm = '';
  windowEnabled = false;
  windowMonths = 3;
  readonly windowPresets = [1, 3, 6];

  // Table state
  displayRows: JobChangeRow[] = [];
  tableLoading = true;
  pageLoading = false;
  tableError = false;
  hasMore = false;
  /** Server-side count for the current tab + window filters (not used while searching). */
  tableCount?: number;
  searchCapped = false;

  @ViewChild(LedgerDownloadDrawerComponent) downloadDrawer: LedgerDownloadDrawerComponent;

  importsInProgress$?: BehaviorSubject<any>;

  private pageToken?: string;
  // The in-flight table or search request; cancelled whenever the query changes
  private tableRequest?: Subscription;
  // The search term the current table contents were loaded for
  private appliedSearchTerm = '';
  private tableGeneration = 0;
  private countsGeneration = 0;
  private searchInput$ = new Subject<string>();
  private importsWereActive = false;
  private destroyed = new Subject<void>();

  constructor(
    private notify: NotificationService,
    private datePipe: DatePipe,
    private route: ActivatedRoute,
    private ledgerContactsService: LedgerContactsService,
    private ledgersService: LedgersService,
    private hostRef: ElementRef<HTMLElement>,
    @Optional() private ledgerParent: LedgerComponent | null
  ) {}

  get searchActive(): boolean {
    return (
      !this.searchDisabled &&
      this.searchTerm.trim().length >= LedgerOverviewV2Component.MIN_SEARCH_LENGTH
    );
  }

  /** Quick search is disabled on very large ledgers; the Details tab handles those. */
  get searchDisabled(): boolean {
    return (this.contactCount ?? 0) > LedgerOverviewV2Component.SEARCH_MAX_CONTACTS;
  }

  /** True once we know the ledger has no detected changes at all. */
  get emptyLedger(): boolean {
    return (
      this.movedCount !== undefined &&
      this.noEmployerCount !== undefined &&
      this.movedCount + this.noEmployerCount === 0 &&
      this.displayRows.length === 0
    );
  }

  get verifiedPct(): number | null {
    if (!this.contactCount || this.verifiedCount === undefined) {
      return null;
    }
    return Math.round((this.verifiedCount * 100) / this.contactCount);
  }

  get hasActiveFilters(): boolean {
    return this.activeTab !== 'all' || this.searchTerm.trim() !== '' || this.windowEnabled;
  }

  ngOnInit(): void {
    // Get the ledger from the resolver and org ID from the route
    this.ledger = this.route.parent?.snapshot.data.userdata;
    const routeOrg = this.route.parent?.snapshot.paramMap.get('orgId');
    if (routeOrg) {
      this.orgId = routeOrg;
    }
    // Seed from the resolved ledger so size-dependent UI is right before the refresh lands
    this.contactCount = this.ledger?.contact_counts?.total;
    this.verifiedCount = this.ledger?.contact_counts?.verified;

    this.searchInput$
      .pipe(debounceTime(LedgerOverviewV2Component.SEARCH_DEBOUNCE_MS), takeUntil(this.destroyed))
      .subscribe((term: string) => {
        this.searchTerm = term;
        // Below-minimum keystrokes and retyped terms don't change the effective query;
        // don't spawn a reload for them
        const effective = this.searchActive ? term.trim() : '';
        if (effective === this.appliedSearchTerm) {
          return;
        }
        this.appliedSearchTerm = effective;
        this.reloadTable();
      });

    // The parent polls import status; refresh once an import cycle finishes so new
    // contacts and changes show up without a manual reload.
    this.importsInProgress$ = this.ledgerParent?.importsInProgress$;
    this.importsInProgress$?.pipe(takeUntil(this.destroyed)).subscribe((status: any) => {
      const active = !!(status?.importing || status?.processing);
      if (this.importsWereActive && !active) {
        this.refreshData();
      }
      this.importsWereActive = active;
    });

    this.refreshData();
  }

  ngAfterViewInit(): void {
    // Measure once the first layout has settled
    setTimeout(() => this.updateViewportOffset());
  }

  ngOnDestroy(): void {
    this.destroyed.next();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportOffset();
  }

  /**
   * The header above this page (shell padding, breadcrumb, description, tab bar) varies
   * in height, so measure where the component actually starts and expose it as a CSS
   * variable; the stylesheet sizes the page to the viewport from there.
   */
  private updateViewportOffset(): void {
    const host = this.hostRef.nativeElement;
    // Undo any scroll already applied by ancestor scrollers so the offset reflects the
    // scrolled-to-top position
    let scrolled = 0;
    let parent = host.parentElement;
    while (parent) {
      scrolled += parent.scrollTop || 0;
      parent = parent.parentElement;
    }
    const top = host.getBoundingClientRect().top + scrolled;
    host.style.setProperty('--overview-top', Math.max(0, Math.round(top)) + 'px');
  }

  refreshData(): void {
    this.loadLedgerCounts();
    this.loadChangeCounts();
    this.reloadTable();
  }

  setTab(tab: ChangeTab): void {
    if (tab === this.activeTab) {
      return;
    }
    this.activeTab = tab;
    this.reloadTable();
  }

  onSearchInput(value: string): void {
    if (this.searchDisabled) {
      return;
    }
    this.searchInput$.next(value);
  }

  toggleWindow(enabled: boolean): void {
    this.windowEnabled = enabled;
    this.reloadTable();
  }

  onWindowMonthsChange(value: string | number): void {
    const months = Math.floor(Number(value));
    if (!Number.isFinite(months) || months < 1) {
      return;
    }
    this.windowMonths = Math.min(months, 120);
    // The moved-recently card count depends on the window length
    this.loadChangeCounts();
    if (this.windowEnabled) {
      this.reloadTable();
    }
  }

  setWindowPreset(months: number): void {
    const changed = this.windowMonths !== months || !this.windowEnabled;
    this.windowMonths = months;
    this.windowEnabled = true;
    if (changed) {
      this.loadChangeCounts();
      this.reloadTable();
    }
  }

  clearFilters(): void {
    this.activeTab = 'all';
    this.searchTerm = '';
    this.appliedSearchTerm = '';
    // Supersede any keystrokes still waiting in the debounce so they can't re-apply
    // the search after clearing
    this.searchInput$.next('');
    this.windowEnabled = false;
    this.reloadTable();
  }

  onTableScroll(event: any): void {
    const el = event?.target;
    if (!el || this.searchActive || !this.hasMore || this.pageLoading || this.tableLoading) {
      return;
    }
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < LedgerOverviewV2Component.SCROLL_LOAD_THRESHOLD_PX) {
      this.loadNextPage();
    }
  }

  loadNextPage(): void {
    if (this.searchActive || !this.hasMore || this.pageLoading || this.tableLoading) {
      return;
    }
    this.pageLoading = true;
    this.fetchPage(this.tableGeneration, this.pageToken);
  }

  trackRow(index: number, row: JobChangeRow): string | number {
    return row.id ?? index;
  }

  // Downloads are disabled while a search is active: the flat request operator can't
  // combine the OR across searched fields with the page's AND scoping, so an export
  // could not match what the table shows.
  openDownloadDrawer(): void {
    this.downloadDrawer.open('view', this.buildTableFilters(), this.tableCount ?? null);
  }

  /** Whitespace-separated search terms; each is its own value, and any value matches. */
  private searchTerms(): string[] {
    return this.searchTerm
      .trim()
      .split(/\s+/)
      .filter((term) => term.length > 0);
  }

  private loadLedgerCounts(): void {
    this.ledgersService
      .getLedgerById(this.orgId, this.ledger.id)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (res: Ledger) => {
          this.contactCount = res.contact_counts?.total ?? 0;
          this.verifiedCount = res.contact_counts?.verified ?? 0;
        },
        error: (err: any) => {
          // Keep the last known counts: zeroing them would blank the cards and wrongly
          // re-enable quick search on very large ledgers
          this.contactCount = this.contactCount ?? 0;
          this.verifiedCount = this.verifiedCount ?? 0;
          if (err.status === 404) {
            // The ledger may not be queryable yet right after creation; retry shortly
            timer(10000)
              .pipe(takeUntil(this.destroyed), take(1))
              .subscribe(() => this.refreshData());
            return;
          }
          this.notify.error('Oops. There was an error getting the data. Please try again later.');
        },
      });
  }

  /** Refreshes the server-side change counts the stat cards display. */
  private loadChangeCounts(): void {
    const generation = ++this.countsGeneration;
    this.fetchCount(
      [this.changedExistsFilter(), this.employedFilter(true)],
      generation,
      (count) => (this.movedCount = count)
    );
    this.fetchCount(
      [this.changedExistsFilter(), this.employedFilter(false)],
      generation,
      (count) => (this.noEmployerCount = count)
    );
    this.fetchCount(
      [this.changedExistsFilter(), this.employedFilter(true), this.startedWithinFilter()],
      generation,
      (count) => (this.movedRecentCount = count)
    );
  }

  private fetchCount(
    filters: ContactSearchFilter[],
    generation: number,
    assign: (count: number) => void
  ): void {
    const request: SearchLedgerContactsRequest = {
      filters: filters,
      view: SearchLedgerContactsRequest.ViewEnum.Count,
    };
    this.ledgerContactsService
      .searchLedgerContacts(this.ledger.id, this.orgId, request)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (res: SearchLedgerContacts200Response) => {
          if (generation !== this.countsGeneration) {
            return;
          }
          assign(typeof res.count === 'number' ? res.count : 0);
        },
        error: () => {
          // Leave the previous value in place: a failed count is not a zero count, and
          // zeroing the change counts would falsely trigger the empty-ledger state
        },
      });
  }

  private reloadTable(): void {
    this.tableRequest?.unsubscribe();
    const generation = ++this.tableGeneration;
    this.tableLoading = true;
    this.tableError = false;
    this.displayRows = [];
    this.pageToken = undefined;
    this.hasMore = false;
    this.pageLoading = false;
    this.searchCapped = false;
    if (this.searchActive) {
      this.runSearch(generation);
    } else {
      this.tableCount = undefined;
      this.fetchPage(generation, undefined);
    }
  }

  private fetchPage(generation: number, paginationToken?: string): void {
    const request: SearchLedgerContactsRequest = {
      filters: this.buildTableFilters(),
      size: LedgerOverviewV2Component.PAGE_SIZE,
      pagination_token: paginationToken,
      sort_by: LedgerOverviewV2Component.DEFAULT_SORT,
    };
    this.tableRequest = this.ledgerContactsService
      .searchLedgerContacts(this.ledger.id, this.orgId, request)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (res: SearchLedgerContacts200Response) => {
          if (generation !== this.tableGeneration) {
            return; // a newer load superseded this one
          }
          const contacts = res.contacts || [];
          this.displayRows = this.displayRows.concat(
            contacts.map((contact: Contact) => this.toRow(contact))
          );
          if (typeof res.count === 'number') {
            this.tableCount = res.count;
          }
          this.pageToken = res.pagination_token || undefined;
          this.hasMore = !!this.pageToken && contacts.length > 0;
          this.tableLoading = false;
          this.pageLoading = false;
        },
        error: () => {
          if (generation !== this.tableGeneration) {
            return;
          }
          this.tableError = this.displayRows.length === 0;
          this.tableLoading = false;
          this.pageLoading = false;
          this.notify.error('Oops. There was an error getting the data.');
        },
      });
  }

  private runSearch(generation: number): void {
    const terms = this.searchTerms();
    const requests = LedgerOverviewV2Component.SEARCH_FIELDS.map((field) => {
      const filters = this.buildTableFilters();
      filters.push({
        field: field,
        match_type: ContactSearchFilter.MatchTypeEnum.Fuzzy,
        type: ContactSearchFilter.TypeEnum.Must,
        string_values: terms,
      });
      const request: SearchLedgerContactsRequest = {
        filters: filters,
        size: LedgerOverviewV2Component.SEARCH_CAP,
        sort_by: LedgerOverviewV2Component.DEFAULT_SORT,
      };
      return this.ledgerContactsService.searchLedgerContacts(this.ledger.id, this.orgId, request);
    });

    this.tableRequest = forkJoin(requests)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (responses: SearchLedgerContacts200Response[]) => {
          if (generation !== this.tableGeneration) {
            return;
          }
          const byKey = new Map<string, JobChangeRow>();
          let capped = false;
          responses.forEach((res, index) => {
            capped = capped || !!res.pagination_token;
            (res.contacts || []).forEach((contact: Contact) => {
              const row = this.toRow(contact);
              const key = row.id || row.linkedin || index + ':' + row.name;
              if (!byKey.has(key)) {
                byKey.set(key, row);
              }
            });
          });
          const merged = Array.from(byKey.values());
          merged.sort((a, b) => this.compareRows(a, b));
          this.displayRows = merged;
          this.searchCapped = capped;
          this.tableLoading = false;
        },
        error: () => {
          if (generation !== this.tableGeneration) {
            return;
          }
          this.tableError = true;
          this.tableLoading = false;
          this.notify.error('Oops. There was an error getting the data.');
        },
      });
  }

  private buildTableFilters(): ContactSearchFilter[] {
    const filters = [this.changedExistsFilter()];
    if (this.activeTab === 'new_company') {
      filters.push(this.employedFilter(true));
    } else if (this.activeTab === 'no_employer') {
      filters.push(this.employedFilter(false));
    }
    if (this.windowEnabled) {
      filters.push(this.startedWithinFilter());
    }
    return filters;
  }

  private changedExistsFilter(): ContactSearchFilter {
    return {
      field: 'last_company_changed_at',
      match_type: ContactSearchFilter.MatchTypeEnum.Exists,
      type: ContactSearchFilter.TypeEnum.Must,
    };
  }

  // Contacts who left the workforce get the literal string 'None' as their current
  // company name, so the buckets match on that keyword rather than field existence
  private employedFilter(employed: boolean): ContactSearchFilter {
    return {
      field: 'current_position.company.name',
      match_type: ContactSearchFilter.MatchTypeEnum.Exact,
      type: employed ? ContactSearchFilter.TypeEnum.MustNot : ContactSearchFilter.TypeEnum.Must,
      string_values: [LedgerOverviewV2Component.NO_EMPLOYER_KEYWORD],
    };
  }

  private startedWithinFilter(): ContactSearchFilter {
    // Start dates only carry month granularity, so the window is calendar months: the
    // current month plus the windowMonths - 1 before it, from the first of that month.
    // Exception: early in the month a 1-month window would be nearly empty, so through
    // the 14th it reaches back to the previous month as well.
    const now = new Date();
    let monthsBack = this.windowMonths - 1;
    if (
      this.windowMonths === 1 &&
      now.getUTCDate() <= LedgerOverviewV2Component.EARLY_MONTH_GRACE_DAY
    ) {
      monthsBack = 1;
    }
    const cutoff = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - monthsBack, 1));
    return {
      field: 'current_position.started_at',
      type: ContactSearchFilter.TypeEnum.Must,
      date_from: this.datePipe.transform(cutoff, 'yyyy-MM-dd', 'UTC') || undefined,
    };
  }

  /** Most recent start first, matching DEFAULT_SORT; rows without a date sort last. */
  private compareRows(a: JobChangeRow, b: JobChangeRow): number {
    if (a.startedTs === null && b.startedTs === null) {
      return 0;
    }
    if (a.startedTs === null) {
      return 1;
    }
    if (b.startedTs === null) {
      return -1;
    }
    return b.startedTs - a.startedTs;
  }

  private toRow(contact: Contact): JobChangeRow {
    const current = contact.current_position;
    const rawCompany = current?.company?.name || null;
    const employed = !!rawCompany && rawCompany !== LedgerOverviewV2Component.NO_EMPLOYER_KEYWORD;
    const kind: ChangeKind = employed ? 'new_company' : 'no_employer';
    // Don't surface the 'None' keyword itself in the table or CSV
    const newCompany = employed ? rawCompany : null;
    const startedAt = current?.started_at || null;
    const parsedStarted = startedAt ? Date.parse(startedAt) : NaN;
    const startedTs = Number.isNaN(parsedStarted) ? null : parsedStarted;
    const changedAt = contact.last_company_changed_at || null;
    const parsedChanged = changedAt ? Date.parse(changedAt) : NaN;
    const changedTs = Number.isNaN(parsedChanged) ? 0 : parsedChanged;
    const monthsAgo = startedTs !== null ? this.monthsSince(startedTs) : null;
    const olderMove =
      kind === 'new_company' &&
      monthsAgo !== null &&
      monthsAgo > LedgerOverviewV2Component.OLDER_MOVE_MONTHS;

    return {
      id: contact.id,
      name: contact.name || contact.imported?.name || 'Unknown',
      linkedin: contact.linkedin || contact.imported?.linkedin || null,
      importedCompany: contact.imported?.company || null,
      importedTitle: contact.imported?.title || null,
      newCompany: newCompany,
      newTitle: employed ? current?.title || null : null,
      kind: kind,
      olderMove: olderMove,
      startedAt: startedAt,
      startedTs: startedTs,
      startedAgo: monthsAgo !== null ? this.monthsAgoLabel(monthsAgo) : null,
      changedAt: changedAt,
      changedTs: changedTs,
    };
  }

  /** Calendar months (UTC) between the given timestamp and now. */
  private monthsSince(ts: number): number {
    const now = new Date();
    const then = new Date(ts);
    return (
      (now.getUTCFullYear() - then.getUTCFullYear()) * 12 + (now.getUTCMonth() - then.getUTCMonth())
    );
  }

  // Start dates only carry month granularity, so the relative label speaks in months
  private monthsAgoLabel(months: number): string {
    if (months < 0) {
      return 'upcoming';
    }
    if (months === 0) {
      return 'this month';
    }
    if (months === 1) {
      return 'last month';
    }
    if (months < 12) {
      return months + ' months ago';
    }
    const years = Math.round((months / 12) * 10) / 10;
    if (years === 1) {
      return '1 year ago';
    }
    return years + ' years ago';
  }
}
